import { useNavigationSettingsContext } from '@app/components/settings/navigation/NavigationSettingsContext';
import { Button } from '@app/components/various/Button';
import { FieldLabel, Form, FormFieldset, FormGrid, InputField } from '@app/components/various/Form';
import { Edit } from '@app/components/various/Icons';
import { Popover } from '@app/components/various/Popover';
import { TextEllipsis } from '@app/components/various/TextEllipsis';
import { INPUT_URL_MAX_LENGTH } from '@app/constants/limits';
import { getMarkets } from '@app/ducks';
import { useValidatedForm } from '@app/utils/hooks/useValidatedForm';
import { getUrlSchema } from '@app/utils/schemas';
import { Navigation } from '@typings';
import { nanoid } from 'nanoid';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import * as yup from 'yup';

import { MarketSelectForm } from './MarketSelectForm';

export interface LinkFormFields {
  label: string;
  url: string;
  markets: string[];
}

export const LinkForm = () => {
  const { addedMenuItems, areInputsDisabled, formMethods: navigationFormMethods, scrollAddedMenuItems } = useNavigationSettingsContext();
  const allMarkets = useSelector(getMarkets);
  const { t } = useTranslation(['validation', 'common', 'cms', 'settings']);

  const validationSchema: yup.ObjectSchema<LinkFormFields> = yup.object({
    label: yup.string().trim().required(t('validation:navigation_label_hint')),
    markets: yup.array(yup.string().defined()).min(1, t('validation:min_one_market_hint')).defined(),
    url: getUrlSchema({ hasConfidentialIdCheck: true, isCmsEditorCheck: true }),
  });

  const formMethods = useValidatedForm<LinkFormFields>({
    defaultValues: {
      label: '',
      markets: allMarkets.map(market => market.id),
      url: '',
    },
    validationSchema,
  });
  const { register, reset, setValue, watch } = formMethods;
  const selectedMarketIds = watch('markets');
  const selectedMarkets = React.useMemo(
    () => allMarkets.filter(market => selectedMarketIds.includes(market.id)),
    [selectedMarketIds, allMarkets],
  );

  const handleSubmit = (values: LinkFormFields) => {
    reset();

    const newMenuItem: Navigation.LinkMenuItem = {
      ...values,
      id: nanoid(),
      openNewTab: false,
      pageId: null,
      type: 'link',
    };

    navigationFormMethods.setValue('navigationItems', [...addedMenuItems, newMenuItem], { shouldDirty: true });
    scrollAddedMenuItems();
  };

  return (
    <>
      <Form formMethods={formMethods} onSubmit={handleSubmit}>
        <FormFieldset className="" isDisabled={areInputsDisabled}>
          <FormGrid>
            <InputField label={t('settings:navigation_label')} size="medium" isRequired {...register('label')} />
            <InputField type="url" label={t('common:url')} size="medium" maxLength={INPUT_URL_MAX_LENGTH} isRequired {...register('url')} />
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between gap-1">
                <FieldLabel label={t('cms:market_other')} />
                <Popover
                  isClickable
                  contentHeader={t('settings:market_select_title')}
                  content={({ handleClose }) => {
                    return (
                      <MarketSelectForm
                        onClose={handleClose}
                        onSubmit={markets => {
                          setValue('markets', markets);
                          handleClose();
                        }}
                        selectedMarkets={selectedMarketIds}
                      />
                    );
                  }}
                >
                  <Button color="secondary" variant="text" icon={Edit}>
                    {t('common:edit')}
                  </Button>
                </Popover>
              </div>
              <TextEllipsis className="text-neutral-40 body-5" noWrapText={false} lines={2}>
                {selectedMarkets.length === allMarkets.length ?
                  t('settings:all_markets')
                : selectedMarkets.map(market => market.name).join(', ')}
              </TextEllipsis>
            </div>
          </FormGrid>
          <Button type="submit" size="medium" className="ml-auto mt-4">
            {t('common:add_item')}
          </Button>
        </FormFieldset>
      </Form>
    </>
  );
};
